import { Add } from "@mui/icons-material";
import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useWatch } from "react-hook-form";
import { FormAutoComplete } from "../../../../components/form";
import { GetCIAP2 } from "@/services/terminology/GetCIAP2";
import { toast } from "react-toastify";

interface ProblemOption {
  display?: string;
  value?: string;
  code?: string;
}

interface AddProblemaProps {
  /**
   * Problemas em aberto já calculados em `Problemas.tsx`. Comparar sobre os
   * dados evita o falso negativo do texto truncado na tela e o falso positivo
   * de ler qualquer Typography descendente (data, especialidade, profissional).
   */
  openItens?: string[];
}

/** Normalização única usada na comparação de duplicatas. */
const normalize = (value?: string) => value?.trim()?.toLowerCase() || "";

export function AddProblema({ openItens = [] }: AddProblemaProps) {
  const { control, unregister, problems, setProblems, setValue } =
    useMedicalCareContext();
  const [currentIndex, setCurrentIndex] = useState<number>(
    problems?.length || 0
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [searchProblems, setSearchProblems] = useState<string>("");
  const [problemsOptions, setProblemsOptions] = useState<any>();
  const formValues = useWatch({
    name: "New_Problema",
    control,
  }) as ProblemOption[];

  // Mapeia as opções de CIAP2
  const problemsList =
    problemsOptions?.expansion?.contains?.map((el: any) => ({
      ...el,
      label: `${el?.display} - ${el?.code}`,
      value: el?.code,
    })) || [];

  // Texto livre abandonado no campo virava Condition CIAP2 real no Footer
  // (`[...problems, formData?.freeSoloProblem]`): limpa ao sair da aba.
  useEffect(() => {
    return () => {
      setValue(`freeSoloProblem`, undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flag para controlar se o searchProblems deve ser processado
  const shouldProcessSearchRef = useRef(true);
  
  useEffect(() => {
    // Se não devemos processar (porque acabamos de limpar), ignore este ciclo
    if (!shouldProcessSearchRef.current) {
      shouldProcessSearchRef.current = true;
      return;
    }
    
    async function fetchProblems() {
      const valueSetResponse = await GetCIAP2({
        filter: searchProblems,
      });
      setProblemsOptions(valueSetResponse?.data);
      setLoading(false);
    }

    const debounce = setTimeout(() => {
      if (searchProblems) {
        setLoading(true);
        fetchProblems();
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [searchProblems]);

  const handleAddProblema = () => {
    const currentValue = formValues?.[currentIndex];
    if (currentValue?.display || currentValue?.value || searchProblems) {
      const problemValue =
      currentValue?.display || currentValue?.value || searchProblems;
      
      if (problemValue?.trim()) {
        const problemValueKey = normalize(problemValue);

        const existsInCurrentList = problems?.some(
          (problem: string) => normalize(problem) === problemValueKey
        );

        const existsInOpenList = openItens?.some(
          (problem: string) => normalize(problem) === problemValueKey
        );

        if (existsInCurrentList || existsInOpenList) {
          // Bloqueia a inclusão e PRESERVA o que foi digitado.
          toast.warning(`"${problemValue}" já está na lista de problemas`, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });

          return;
        }

        setProblems((prev: string[]) => [...prev, problemValue]);
        setValue(`New_Problema.${currentIndex}`, null);
        setCurrentIndex((prev: number) => prev + 1);
        setSearchProblems("");
      }
    }
    //Set value abaixo corrige duplicação de problemas
    setValue(`freeSoloProblem`, undefined);
  };

  return (
    <Stack spacing={2}>
      {problems?.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          paddingBottom={1}
          gap={1}
        >
          {problems.map(
            (problema: string, index: number) =>
              problema?.length > 0 && (
                <Chip
                  key={index}
                  sx={{ padding: "8px", height: "auto" }}
                  label={
                    problema.length > 85 ? (
                      <Tooltip
                        placement="right"
                        title={problema}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [80, 20] },
                              },
                            ],
                          },
                        }}
                      >
                        <Typography>{problema.substring(0, 85)}...</Typography>
                      </Tooltip>
                    ) : (
                      <Typography>{problema}</Typography>
                    )
                  }
                  onDelete={() => {
                    setProblems((prev: string[]) => {
                      const updated = [...prev];
                      updated[index] = "";
                      return updated;
                    });
                    unregister(`New_Problema.${index}`);
                  }}
                />
              )
          )}
        </Stack>
      )}

      <Stack spacing={1}>
        <FormAutoComplete
          key={`problema-input-${currentIndex}`}
          freeSolo
          loading={loading}
          name={`New_Problema.${currentIndex}`}
          label="Problema"
          control={control}
          onInputChange={(e: any) => {
            if (e?.target?.value?.length) {
              const value = e?.target?.value;
              setSearchProblems(value);
              setValue(`freeSoloProblem`, value);
            } else {
              setSearchProblems("");
              // Campo esvaziado pelo usuário: sem esta limpeza o texto abandonado
              // continuava no formulário e virava diagnóstico no Footer.
              setValue(`freeSoloProblem`, undefined);
            }
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddProblema();
            }
          }}
          selectOptions={problemsList}
          autoFocus
        />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            sx={{ width: "fit-content" }}
            onClick={handleAddProblema}
            variant="text"
          >
            <Add />
            Adicionar
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
